import { None } from "./types";
import { DictionaryProvider, DictionaryConsumer } from "./Dictionary";
import { Scalar, ScalarProvider, ScalarConsumer, scalarValue } from "./Scalar";

function getSingleton<K, V>(source: Scalar<DictionaryProvider<K, V>>) {
	const entries = scalarValue(source).entries;
	if (entries.size === 1) {
		return [...entries][0][1];
	} else {
		return None;
	}
}

export class GetSingleton<KeyType, ValueType>
	extends ScalarProvider<ValueType | None>
	implements ScalarConsumer<DictionaryProvider<KeyType, ValueType>>, DictionaryConsumer<KeyType, ValueType>
{
	private _subscriber: ScalarConsumer<ValueType | None> | undefined;
	private _dictionary_source: DictionaryProvider<KeyType, ValueType> | undefined;
	private _value: ValueType | None | undefined;

	constructor(readonly scalar_source: Scalar<DictionaryProvider<KeyType, ValueType>>, readonly __origin?: string) {
		super();
	}

	private ensure(value: ValueType | None) {
		if (this._value === value) return;
		if (this._value instanceof ScalarProvider) {
			this._value.unsubscribe(this._subscriber!);
		}
		this._value = value;
		if (this._value instanceof ScalarProvider) {
			this._value.subscribe(this._subscriber!);
		} else {
			this._subscriber!.update(value);
		}
	}

	get value() {
		if (this._subscriber) {
			return this._value!;
		} else {
			return getSingleton(this.scalar_source);
		}
	}

	update(value: DictionaryProvider<KeyType, ValueType>) {
		this._dictionary_source?.unsubscribe(this);
		this._dictionary_source = value;
		this._dictionary_source.subscribe(this);
		this.ensure(getSingleton(this.scalar_source));
	}

	add() {
		this.ensure(getSingleton(this.scalar_source));
	}

	remove() {
		this.ensure(getSingleton(this.scalar_source));
	}

	subscribe(subscriber: ScalarConsumer<ValueType | None>) {
		if (this._subscriber !== undefined) throw new Error("unexpected");
		this._subscriber = subscriber;
		if (this.scalar_source instanceof ScalarProvider) {
			this.scalar_source.subscribe(this);
		} else {
			this.update(this.scalar_source);
		}
		return subscriber;
	}

	unsubscribe(subscriber: ScalarConsumer<ValueType | None>) {
		if ((<any>global)?.alan_strict && this._subscriber !== subscriber) throw new Error("unexpected");
		this._subscriber = undefined;
		if (this._value instanceof ScalarProvider) {
			this._value.unsubscribe(subscriber);
		}
		if (this.scalar_source instanceof ScalarProvider) {
			this.scalar_source.unsubscribe(this);
		}
		this._dictionary_source!.unsubscribe(this);
		this._dictionary_source = undefined;
	}
}
